import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * AutoProvisioning Component
 * Main container component for the auto-provisioning workflow
 * Uses a stacked layout where each step remains visible as the process progresses
 */
import { useEffect } from "react";
import { useAutoProvisioning } from "../hooks/useAutoProvisioning";
import { FileUpload } from "./FileUpload";
import { WorkflowHeader } from "./WorkflowHeader";
import { ValidationStep } from "./ValidationStep";
import { ValidationErrorsDisplay } from "./ValidationErrorsDisplay";
import { AccountMappingStep } from "./AccountMappingStep";
import { UploadStep } from "./UploadStep";
import { UserCreationStep } from "./UserCreationStep";
import { PractitionerMessages } from "./PractitionerMessages";
import { CopyReferenceUserStep } from "./CopyReferenceUserStep";
import { CompletionStatus } from "./CompletionStatus";
import { ErrorStatus } from "./ErrorStatus";
import { downloadValidationErrorsReport, downloadProvisioningErrorsReport, downloadProvisioningResultsReport, hasProvisioningErrors, } from "../utils/errorReportGenerator";
import { downloadResultsXlsx } from "../utils/resultsXlsxGenerator";
import "../styles/AutoProvisioning.css";
export const AutoProvisioning = ({ onComplete }) => {
    const provisioning = useAutoProvisioning();
    // Determine which steps have been reached/completed
    const stepOrder = [
        "upload",
        "validation",
        "account-mapping",
        "uploading",
        "job-monitoring",
        "copy-reference-user",
        "complete",
    ];
    const currentStepIndex = stepOrder.indexOf(provisioning.currentStep);
    const hasStartedProcessing = currentStepIndex > 0;
    const hasPassedValidation = currentStepIndex >= 2;
    const hasPassedAccountMapping = currentStepIndex >= 3;
    const hasPassedUpload = currentStepIndex >= 4;
    const hasPassedJobMonitoring = currentStepIndex >= 5;
    const hasPassedCopyUser = currentStepIndex >= 6;
    // Helper functions for row status classes
    const getValidationRowClass = () => {
        if (provisioning.validationErrors.length > 0)
            return "error";
        if (provisioning.currentStep === "validation" && provisioning.isValidating)
            return "in-progress";
        if (hasPassedValidation)
            return "complete";
        return "";
    };
    const getAccountMappingRowClass = () => {
        if (provisioning.accountMappingError)
            return "error";
        if (provisioning.currentStep === "account-mapping" && provisioning.isAccountMapping)
            return "in-progress";
        if (hasPassedAccountMapping)
            return "complete";
        return "pending";
    };
    const getUploadRowClass = () => {
        if (provisioning.uploadError)
            return "error";
        if (provisioning.currentStep === "uploading" && provisioning.isUploading)
            return "in-progress";
        if (hasPassedUpload)
            return "complete";
        return "pending";
    };
    const getUserCreationRowClass = () => {
        if (provisioning.monitoringError && provisioning.createdUsers.length === 0)
            return "error";
        if (provisioning.successfullyCreatedCount === 0 && provisioning.hasWarnings)
            return "warning";
        if (provisioning.hasWarnings)
            return "warning";
        if (provisioning.currentStep === "job-monitoring" && provisioning.isMonitoring)
            return "in-progress";
        if (provisioning.successfullyCreatedCount > 0)
            return "complete";
        return "";
    };
    const getCopyReferenceUserRowClass = () => {
        if (provisioning.copyReferenceUserError)
            return "warning";
        if (provisioning.isCopyingReferenceUser)
            return "in-progress";
        if (provisioning.copyReferenceUserStatus)
            return "complete";
        return "";
    };
    // Auto-start monitoring when we have a job ID
    useEffect(() => {
        if (provisioning.currentStep === "job-monitoring" && provisioning.jobId && !provisioning.isMonitoring) {
            provisioning.startMonitoring();
        }
    }, [provisioning.currentStep, provisioning.jobId, provisioning.isMonitoring]);
    // Auto-start copy reference user when step changes to copy-reference-user
    useEffect(() => {
        if (provisioning.currentStep === "copy-reference-user" &&
            !provisioning.isCopyingReferenceUser &&
            !provisioning.copyReferenceUserStatus &&
            !provisioning.copyReferenceUserError) {
            provisioning.startCopyReferenceUser();
        }
    }, [
        provisioning.currentStep,
        provisioning.isCopyingReferenceUser,
        provisioning.copyReferenceUserStatus,
        provisioning.copyReferenceUserError,
    ]);
    // Show upload screen if we haven't started processing yet
    if (!hasStartedProcessing && provisioning.currentStep !== "error") {
        return (_jsx("div", { className: "auto-provisioning", children: _jsxs("div", { className: "auto-provisioning__content", children: [_jsx(WorkflowHeader, {}), _jsx(FileUpload, { onFileSelect: provisioning.parseFile, isLoading: provisioning.isParsing, error: provisioning.parseError })] }) }));
    }
    // Stacked workflow view - show all steps that have been reached
    return (_jsx("div", { className: "auto-provisioning", children: _jsxs("div", { className: "auto-provisioning__workflow-stack", children: [_jsx(WorkflowHeader, {}), _jsx(ValidationStep, { isValidating: provisioning.isValidating, validationErrors: provisioning.validationErrors, hasPassedValidation: hasPassedValidation, validationCurrentRow: provisioning.validationCurrentRow, validationTotalRows: provisioning.validationTotalRows, recordsCount: provisioning.records.length, isValidationOnly: provisioning.isValidationOnly, currentStep: provisioning.currentStep, onUploadNewFile: () => provisioning.setCurrentStep("upload"), onContinue: provisioning.continueAfterValidation, rowClassName: getValidationRowClass() }), _jsx(ValidationErrorsDisplay, { validationErrors: provisioning.validationErrors, records: provisioning.records, onDownloadErrors: downloadValidationErrorsReport }), (hasPassedValidation || provisioning.currentStep === "account-mapping") &&
                    provisioning.validationErrors.length === 0 && (_jsx(AccountMappingStep, { isAccountMapping: provisioning.isAccountMapping, accountMappingError: provisioning.accountMappingError, accountMappingResult: provisioning.accountMappingResult, hasPassedAccountMapping: hasPassedAccountMapping, currentStep: provisioning.currentStep, rowClassName: getAccountMappingRowClass() })), (hasPassedAccountMapping || provisioning.currentStep === "uploading") &&
                    provisioning.validationErrors.length === 0 && (_jsx(UploadStep, { isUploading: provisioning.isUploading, uploadError: provisioning.uploadError, hasPassedUpload: hasPassedUpload, jobId: provisioning.jobId, currentStep: provisioning.currentStep, onRetry: provisioning.startUpload, rowClassName: getUploadRowClass() })), (hasPassedUpload || provisioning.currentStep === "job-monitoring") && (_jsx(UserCreationStep, { isMonitoring: provisioning.isMonitoring, monitoringError: provisioning.monitoringError, createdUsers: provisioning.createdUsers, successfullyCreatedCount: provisioning.successfullyCreatedCount, hasWarnings: provisioning.hasWarnings, jobStatus: provisioning.jobStatus, currentStep: provisioning.currentStep, rowClassName: getUserCreationRowClass() })), _jsx(PractitionerMessages, { createdUsers: provisioning.createdUsers, jobId: provisioning.jobId, onDownloadErrors: downloadProvisioningErrorsReport, onDownloadResults: downloadProvisioningResultsReport, hasErrors: hasProvisioningErrors(provisioning.createdUsers) }), (hasPassedJobMonitoring || provisioning.currentStep === "copy-reference-user") && (_jsx(CopyReferenceUserStep, { isCopyingReferenceUser: provisioning.isCopyingReferenceUser, copyReferenceUserError: provisioning.copyReferenceUserError, copyReferenceUserStatus: provisioning.copyReferenceUserStatus, copyUserJobId: provisioning.copyUserJobId, rowClassName: getCopyReferenceUserRowClass() })), (provisioning.currentStep === "complete" || hasPassedCopyUser) && (_jsx(CompletionStatus, { successfullyCreatedCount: provisioning.successfullyCreatedCount, createdUsersCount: provisioning.createdUsers.length, hasWarnings: provisioning.hasWarnings, recordsWithResults: provisioning.recordsWithResults, records: provisioning.records, onDownloadResults: downloadResultsXlsx, onReset: provisioning.reset })), provisioning.currentStep === "error" && (_jsx(ErrorStatus, { uploadError: provisioning.uploadError, monitoringError: provisioning.monitoringError, onReset: provisioning.reset }))] }) }));
};
export default AutoProvisioning;
